/**
 * Плавающая кнопка «Нужна помощь?» (components/HelpFab.tsx): где она стоит.
 *
 * Как виджет обращений CRM (frontend/src/components/feedback/FeedbackWidget.tsx):
 * кнопку можно перетащить — она прилипает к ближайшему краю (левому или
 * правому), высота — где отпустили. Место запоминается в браузере под своим
 * ключом вики. null — место по умолчанию (правый нижний угол).
 *
 * offset — отступ снизу в пикселях НАД нижней панелью телефона (или над
 * «домашней полоской» / низом экрана на компьютере): так кнопка не уезжает
 * под панель вкладок, когда меняется ширина окна.
 */
export interface FabPos {
  side: 'left' | 'right';
  offset: number;
}

export const FAB_POS_KEY = 'fk-hub-help-fab';
/** Сдвиг пальца/мыши меньше этого — это клик, а не перетаскивание. */
export const FAB_DRAG_THRESHOLD = 6;
/** Ближе к низу не ставим — кнопка не должна прилипать к самому краю. */
export const FAB_MIN_OFFSET = 8;

/** Разбор сохранённого места; мусор и старые форматы — место по умолчанию. */
export function parseFabPos(raw: string | null): FabPos | null {
  if (!raw) return null;
  try {
    const v = JSON.parse(raw) as Partial<FabPos> | null;
    if (v && (v.side === 'left' || v.side === 'right') && typeof v.offset === 'number' && Number.isFinite(v.offset)) {
      return { side: v.side, offset: Math.max(FAB_MIN_OFFSET, Math.round(v.offset)) };
    }
  } catch {
    /* не JSON — место по умолчанию */
  }
  return null;
}

export function loadFabPos(): FabPos | null {
  try {
    return parseFabPos(localStorage.getItem(FAB_POS_KEY));
  } catch {
    // приватный режим / хранилище запрещено — место по умолчанию
    return null;
  }
}

/** Запомнить место; null — забыть (вернуть кнопку в правый нижний угол). */
export function saveFabPos(pos: FabPos | null): void {
  try {
    if (pos) localStorage.setItem(FAB_POS_KEY, JSON.stringify(pos));
    else localStorage.removeItem(FAB_POS_KEY);
  } catch {
    // не запомнили — в следующий раз кнопка будет на месте по умолчанию
  }
}

/**
 * Куда «прилипнет» кнопка, когда её отпустили в точке (x, y).
 * baseBottom — «пол» кнопки из CSS (--fk-fab-floor): нижняя панель телефона
 * или «домашняя полоска» (0 на обычном компьютере) плюс подъём над липкой
 * панелью страницы; topReserve — сколько сверху занято шапкой: выше неё
 * кнопку не ставим.
 */
export function fabPosFromDrop(a: {
  x: number;
  y: number;
  viewportW: number;
  viewportH: number;
  size: number;
  baseBottom: number;
  topReserve: number;
}): FabPos {
  const side: FabPos['side'] = a.x < a.viewportW / 2 ? 'left' : 'right';
  const wanted = a.viewportH - a.y - a.size / 2 - a.baseBottom;
  const max = a.viewportH - a.baseBottom - a.size - a.topReserve;
  const offset = Math.round(Math.max(FAB_MIN_OFFSET, Math.min(max, wanted)));
  return { side, offset };
}
